import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { AcceptInvitationModal } from "@/components/collaboration/accept-invitation-modal"
import { Page } from "@/components/page"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Typography } from "@/components/ui/typography"
import { trpc } from "@/lib/trpc"

export const Route = createFileRoute("/_authed/_individual/personal/journal-invitations/accept")({
  component: AcceptInvitationFlow,
})

function AcceptInvitationFlow() {
  const search = Route.useSearch() as { invitationId?: string }
  const invitationId = search.invitationId
  const navigate = useNavigate()
  const [acceptModalOpen, setAcceptModalOpen] = useState(false)
  const [emailMismatch, setEmailMismatch] = useState(false)

  // Fetch all invitations to find the one we're accepting
  const { data, isLoading, error } = trpc.journalInvitations.list.useQuery({
    filter: "received",
  })

  const invitation = data?.received.find((inv) => inv.id === invitationId)

  useEffect(() => {
    if (!isLoading && invitation) {
      // Check if user's email matches (should always match if query returned it)
      if (invitation.status === "PENDING") {
        // Auto-open accept modal
        setAcceptModalOpen(true)
      } else if (invitation.status === "ACCEPTED") {
        toast.info("You've already accepted this invitation")
        navigate({ to: "/personal/journal-invitations" })
      } else if (invitation.status === "CANCELLED") {
        toast.error("This invitation has been cancelled")
        navigate({ to: "/personal/journal-invitations" })
      }
    } else if (!isLoading && !invitation && !error) {
      // Invitation not found in received list
      setEmailMismatch(true)
    }
  }, [invitation, isLoading, navigate, error])

  if (isLoading) {
    return (
      <Page>
        <div className="text-center py-12">
          <Typography as="p">Loading invitation...</Typography>
        </div>
      </Page>
    )
  }

  if (error) {
    return (
      <Page>
        <div className="max-w-2xl mx-auto space-y-4">
          <Typography as="h1" size="2xl">
            Unable to Load Invitation
          </Typography>
          <Alert variant="destructive">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
          <Button
            onClick={() => {
              window.location.href = "/personal/journal-invitations"
            }}
          >
            Go to Journal Invitations
          </Button>
        </div>
      </Page>
    )
  }

  if (emailMismatch) {
    return (
      <Page>
        <div className="max-w-2xl mx-auto space-y-4">
          <Typography as="h1" size="2xl">
            Invitation Not Available
          </Typography>
          <Alert>
            <AlertDescription>
              This invitation is for a different email address, or it may have been cancelled.
              Please check with the person who sent you the invitation.
            </AlertDescription>
          </Alert>
          <Button onClick={() => navigate({ to: "/personal/journal-invitations" })}>
            View My Invitations
          </Button>
        </div>
      </Page>
    )
  }

  if (!invitation) {
    return (
      <Page>
        <div className="max-w-2xl mx-auto space-y-4">
          <Typography as="h1" size="2xl">
            Invitation Not Found
          </Typography>
          <Alert>
            <AlertDescription>
              The invitation you're looking for could not be found. It may have expired or been
              cancelled.
            </AlertDescription>
          </Alert>
          <Button onClick={() => navigate({ to: "/personal/journal-invitations" })}>
            View My Invitations
          </Button>
        </div>
      </Page>
    )
  }

  return (
    <Page>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <Typography as="h1" size="2xl">
            Accept Journal Invitation
          </Typography>
          <Typography as="p" size="sm" className="text-muted-foreground mt-2">
            You've been invited to collaborate on a journal
          </Typography>
        </div>

        <div className="rounded-lg border p-6 space-y-4">
          <div>
            <Typography as="p" className="font-medium">
              From: {invitation.inviterName}
            </Typography>
            <Typography as="p" className="text-muted-foreground">
              Journal: {invitation.journalName}
            </Typography>
            <Typography as="p" className="text-muted-foreground text-sm">
              Estate: {invitation.estateName}
            </Typography>
          </div>

          <Alert>
            <AlertDescription>
              <strong>🔐 PIN Required:</strong> Contact {invitation.inviterName} to obtain the
              4-digit PIN needed to accept this invitation. This additional security step ensures
              only you can accept.
            </AlertDescription>
          </Alert>

          <Button
            onClick={() => setAcceptModalOpen(true)}
            className="w-full"
            disabled={invitation.status !== "PENDING"}
          >
            {invitation.status === "PENDING" ? "Accept Invitation" : `Status: ${invitation.status}`}
          </Button>
        </div>

        {invitation.status === "PENDING" && (
          <AcceptInvitationModal
            open={acceptModalOpen}
            onOpenChange={setAcceptModalOpen}
            invitationId={invitation.id}
            inviterName={invitation.inviterName}
            journalName={invitation.journalName}
          />
        )}
      </div>
    </Page>
  )
}
